/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { forwardRef, Ref } from 'react';
import { ToggleButton as AriaToggleButton } from 'react-aria-components';
import type { ToggleButtonProps } from './types';
import { useDefinition } from '../../hooks/useDefinition';
import { ToggleButtonDefinition } from './definition';

/**
 * A button that maintains a pressed or unpressed state, with optional start and end icon slots.
 *
 * @public
 */
export const ToggleButton = forwardRef(
  (props: ToggleButtonProps, ref: Ref<HTMLButtonElement>) => {
    const { ownProps, restProps, dataAttributes } = useDefinition(
      ToggleButtonDefinition,
      props,
    );
    const { classes, children, iconStart, iconEnd } = ownProps;

    return (
      <AriaToggleButton
        className={classes.root}
        ref={ref}
        {...dataAttributes}
        {...restProps}
      >
        {renderProps => {
          // If children is a function, call it with render props; otherwise use children as-is
          const renderedChildren =
            typeof children === 'function' ? children(renderProps) : children;

          return (
            <span className={classes.content} data-slot="content">
              {iconStart}
              {renderedChildren}
              {iconEnd}
            </span>
          );
        }}
      </AriaToggleButton>
    );
  },
);

ToggleButton.displayName = 'ToggleButton';
